import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaChevronRight,
  FaArrowLeft,
  FaFutbol,
  FaEdit,
  FaHistory,
  FaTrashAlt,
} from "react-icons/fa";
import { PageLoader } from "@/components/ui/page-loader";
import { useGroup } from "@/lib/context/GroupContext";
import { supabase } from "@/lib/supabase";
import { API } from "@/lib/api";
import { parseEventLineup } from "@/lib/v2/lineup";
import {
  fetchCompletedEventsWithMatches,
  fetchOverdueEventsWithoutResult,
  fetchUnlinkedMatches,
  fetchRsvpsForEvent,
  fetchGroupPlayers,
  linkMatchToEvent,
  cancelMatchEvent,
  cancelEventsLinkedToMatches,
  hasPublishedLineup,
  MatchEvent,
  MatchRecord,
  CompletedMatchContext,
  PlayerRecord,
} from "@/lib/api/rsvp";
import {
  fetchMatchResultHistory,
  MatchResultEdit,
} from "@/lib/api/matchResultHistory";
import { resolveMatchPlayerIdMap } from "@/lib/api/matchGuests";
import MatchFormWizard, {
  Goalscorer,
  MatchFormData,
  PlayerWithGames,
  defaultMatchFormData,
} from "@/components/matches/MatchFormWizard";
import MatchCard from "@/components/matches/MatchCard";
import { MatchFilters as MatchFiltersType } from "@/components/matches/MatchFilters";
import ConfirmationModal from "@/components/ConfirmationModal";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { MatchGridCard } from "@/components/v2/matches/MatchGridCard";
import {
  MatchFiltersToolbar,
  MatchViewMode,
} from "@/components/v2/matches/MatchFiltersToolbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useEnhancedToast } from "@/hooks/use-enhanced-toast";
import { usePageTitle } from "@/hooks/usePageTitle";
import { cn, destructiveOutlineBtnClass } from "@/lib/utils";

type PastItem =
  | { kind: "event"; context: CompletedMatchContext }
  | { kind: "legacy"; match: MatchRecord };

type SelectedItem =
  | { kind: "past-event"; context: CompletedMatchContext }
  | { kind: "legacy"; match: MatchRecord };

type MatchWithExtras = MatchRecord & {
  goalscorers?: Goalscorer[] | null;
  youtubelink?: string | null;
};

function isMatchWinner(value: string | null | undefined): value is NonNullable<MatchFormData["winner"]> {
  return value === "A" || value === "B" || value === "Draw";
}

function hasLoggedResult(
  match:
    | (Pick<MatchRecord, "scorea" | "scoreb" | "winner"> & { goalscorers?: unknown })
    | null
    | undefined
): boolean {
  if (!match) return false;
  if (match.scorea != null && match.scoreb != null) return true;
  if (isMatchWinner(match.winner)) return true;
  return Array.isArray(match.goalscorers) && match.goalscorers.length > 0;
}

function sameCalendarDay(isoA: string, isoB: string): boolean {
  return format(parseISO(isoA), "yyyy-MM-dd") === format(parseISO(isoB), "yyyy-MM-dd");
}

/** Attach a lone unlinked match to a completed event on the same day (winner-only, etc.). */
function enrichCompletedWithLegacyMatches(
  completed: CompletedMatchContext[],
  legacy: MatchRecord[]
): { completed: CompletedMatchContext[]; legacy: MatchRecord[] } {
  const remainingLegacy = [...legacy];

  const enriched = completed.map((context) => {
    if (context.match != null || context.event.match_id != null) return context;

    const candidates = remainingLegacy.filter(
      (match) =>
        sameCalendarDay(match.match_date, context.event.event_date) && hasLoggedResult(match)
    );
    if (candidates.length !== 1) return context;

    const [matched] = candidates;
    const idx = remainingLegacy.indexOf(matched);
    if (idx !== -1) remainingLegacy.splice(idx, 1);
    return { ...context, match: matched };
  });

  return { completed: enriched, legacy: remainingLegacy };
}

function matchToFormData(match: MatchWithExtras): MatchFormData {
  return {
    date: format(parseISO(match.match_date), "yyyy-MM-dd"),
    teamA: match.teama,
    teamB: match.teamb,
    scoreA: match.scorea === null ? "" : match.scorea,
    scoreB: match.scoreb === null ? "" : match.scoreb,
    winner: isMatchWinner(match.winner) ? match.winner : undefined,
    goalscorers: Array.isArray(match.goalscorers) ? match.goalscorers : [],
    youtubeLink: match.youtubelink ?? "",
    autoScore: false,
  };
}

function getItemDate(item: PastItem): Date {
  if (item.kind === "event") {
    return parseISO(item.context.event.event_date);
  }
  return parseISO(item.match.match_date);
}

function getItemPlayerIds(item: PastItem): number[] {
  if (item.kind === "legacy") {
    return [...item.match.teama, ...item.match.teamb];
  }
  const { event, match } = item.context;
  if (match) {
    return [...match.teama, ...match.teamb];
  }
  if (hasPublishedLineup(event)) {
    const parsed = parseEventLineup(event.lineup, { playerNameById: new Map() });
    return [...parsed.playerIdsA, ...parsed.playerIdsB];
  }
  return [];
}

const MATCHES_VIEW_STORAGE_KEY = "v2-matches-view";

function readStoredViewMode(): MatchViewMode {
  if (typeof window === "undefined") return "grid";
  return localStorage.getItem(MATCHES_VIEW_STORAGE_KEY) === "list" ? "list" : "grid";
}

function getItemMatch(item: PastItem): MatchRecord | null {
  if (item.kind === "legacy") return item.match;
  return item.context.match;
}

function getItemTeams(item: PastItem): { teamA: number[]; teamB: number[] } {
  if (item.kind === "legacy") {
    return { teamA: item.match.teama, teamB: item.match.teamb };
  }
  const { event, match } = item.context;
  if (match) {
    return { teamA: match.teama, teamB: match.teamb };
  }
  if (hasPublishedLineup(event)) {
    const parsed = parseEventLineup(event.lineup, { playerNameById: new Map() });
    return { teamA: parsed.playerIdsA, teamB: parsed.playerIdsB };
  }
  return { teamA: [], teamB: [] };
}

function getItemGoalscorers(item: PastItem): Goalscorer[] {
  const match = getItemMatch(item);
  if (!match) return [];
  const extras = match as MatchWithExtras;
  return Array.isArray(extras.goalscorers) ? extras.goalscorers : [];
}

function getItemKey(item: PastItem): string {
  return item.kind === "event" ? `event-${item.context.event.id}` : `legacy-${item.match.id}`;
}

function pastItemToSelected(item: PastItem): SelectedItem {
  if (item.kind === "event") {
    return { kind: "past-event", context: item.context };
  }
  return { kind: "legacy", match: item.match };
}

function getSelectableMatchId(item: PastItem): number | null {
  if (item.kind === "legacy") return item.match.id;
  return item.context.match?.id ?? null;
}

function eventToFormData(
  event: MatchEvent,
  match: MatchRecord | null,
  _goingPlayerIds: number[]
): MatchFormData {
  if (match) return matchToFormData(match);

  const teams = hasPublishedLineup(event)
    ? (() => {
        const parsed = parseEventLineup(event.lineup, { playerNameById: new Map() });
        return { teamA: parsed.playerIdsA, teamB: parsed.playerIdsB };
      })()
    : { teamA: [] as number[], teamB: [] as number[] };

  return {
    date: format(parseISO(event.event_date), "yyyy-MM-dd"),
    teamA: teams.teamA,
    teamB: teams.teamB,
    scoreA: "",
    scoreB: "",
    winner: undefined,
    goalscorers: [],
    youtubeLink: "",
    autoScore: false,
  };
}

export default function MatchesPageV2() {
  const { t } = useTranslation();
  const { currentGroup } = useGroup();
  const { success, error: toastError } = useEnhancedToast();

  const groupId = currentGroup?.id;

  const [loading, setLoading] = useState(true);
  const [pastItems, setPastItems] = useState<PastItem[]>([]);
  const [players, setPlayers] = useState<PlayerWithGames[]>([]);
  const [playerNames, setPlayerNames] = useState<PlayerRecord[]>([]);
  const [selected, setSelected] = useState<SelectedItem | null>(null);
  const [detailHistory, setDetailHistory] = useState<MatchResultEdit[]>([]);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState<"create" | "edit">("create");
  const [wizardInitial, setWizardInitial] = useState<MatchFormData | undefined>();
  const [wizardPrefillTeams, setWizardPrefillTeams] = useState<
    { teamA: number[]; teamB: number[] } | undefined
  >();
  const [wizardContext, setWizardContext] = useState<{
    eventId?: string;
    matchId?: number;
  }>({});
  const [wizardEventGuests, setWizardEventGuests] = useState<{ name: string }[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [dismissModalOpen, setDismissModalOpen] = useState(false);
  const [dismissTarget, setDismissTarget] = useState<{
    eventId: string;
    matchId?: number;
  } | null>(null);
  const [filters, setFilters] = useState<MatchFiltersType>({
    search: "",
    players: [],
    sortBy: "date-desc",
    dateRange: { start: null, end: null },
  });
  const [selectedMatchIds, setSelectedMatchIds] = useState<number[]>([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<MatchViewMode>(readStoredViewMode);

  usePageTitle(`${t("v2.nav.matches")} — ${t("app.name")}`);

  useEffect(() => {
    localStorage.setItem(MATCHES_VIEW_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const getPlayerName = useCallback(
    (id: number): string => {
      const player = players.find((p) => p.id === id);
      return player ? player.name : `Player ${id}`;
    },
    [players]
  );

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!groupId) {
      setPastItems([]);
      setPlayers([]);
      setPlayerNames([]);
      setLoading(false);
      return;
    }

    if (!options?.silent) {
      setLoading(true);
    }
    try {
      const [completed, overdue, legacy, roster, { data: rawMatches, error: mErr }] =
        await Promise.all([
          fetchCompletedEventsWithMatches(groupId),
          fetchOverdueEventsWithoutResult(groupId),
          fetchUnlinkedMatches(groupId),
          fetchGroupPlayers(groupId),
          supabase
            .from("matches")
            .select("teama, teamb")
            .eq("group_id", groupId),
        ]);

      if (mErr) throw mErr;

      setPlayerNames(roster);

      const playersWithGames: PlayerWithGames[] = roster.map((player) => {
        const gamesPlayed = (rawMatches ?? []).reduce((count, match) => {
          if (match.teama.includes(player.id) || match.teamb.includes(player.id)) {
            return count + 1;
          }
          return count;
        }, 0);
        return { ...player, gamesPlayed };
      });
      playersWithGames.sort((a, b) =>
        b.gamesPlayed === a.gamesPlayed
          ? a.name.localeCompare(b.name)
          : b.gamesPlayed - a.gamesPlayed
      );
      setPlayers(playersWithGames);

      const { completed: enrichedCompleted, legacy: remainingLegacy } =
        enrichCompletedWithLegacyMatches(completed, legacy);

      const completedEventIds = new Set(enrichedCompleted.map((c) => c.event.id));
      const overdueOnly = overdue.filter((c) => !completedEventIds.has(c.event.id));

      const past: PastItem[] = [
        ...enrichedCompleted.map((context) => ({ kind: "event" as const, context })),
        ...overdueOnly.map((context) => ({ kind: "event" as const, context })),
        ...remainingLegacy.map((match) => ({ kind: "legacy" as const, match })),
      ];
      past.sort((a, b) => {
        const dateA =
          a.kind === "event"
            ? parseISO(a.context.event.event_date)
            : parseISO(a.match.match_date);
        const dateB =
          b.kind === "event"
            ? parseISO(b.context.event.event_date)
            : parseISO(b.match.match_date);
        return dateB.getTime() - dateA.getTime();
      });
      setPastItems(past);
    } catch (err) {
      console.error("MatchesPage load error:", err);
      toastError({ title: t("errors.error"), description: t("v2.matches.loadError") });
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    load();
  }, [load]);

  const matchYears = useMemo(() => {
    const years = new Set<number>();
    pastItems.forEach((item) => {
      const date = getItemDate(item);
      if (!Number.isNaN(date.getTime())) {
        years.add(date.getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [pastItems]);

  const filteredPastItems = useMemo(() => {
    let result = [...pastItems];

    if (filters.dateRange?.start && filters.dateRange?.end) {
      const startDate = new Date(filters.dateRange.start);
      const endDate = new Date(filters.dateRange.end);
      endDate.setHours(23, 59, 59, 999);
      result = result.filter((item) => {
        const itemDate = getItemDate(item);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    if (filters.search) {
      const searchTerms = filters.search
        .split(",")
        .map((term) => term.trim().toLowerCase())
        .filter((term) => term.length > 0);
      if (searchTerms.length > 0) {
        result = result.filter((item) => {
          const playerNames = getItemPlayerIds(item)
            .map(getPlayerName)
            .join(" ")
            .toLowerCase();
          return searchTerms.some((term) => playerNames.includes(term));
        });
      }
    }

    if (filters.players.length > 0) {
      result = result.filter((item) => {
        const itemPlayers = getItemPlayerIds(item);
        return filters.players.some((playerId) => itemPlayers.includes(playerId));
      });
    }

    result.sort((a, b) => {
      const dateA = getItemDate(a).getTime();
      const dateB = getItemDate(b).getTime();
      if (filters.sortBy === "date-desc") return dateB - dateA;
      if (filters.sortBy === "date-asc") return dateA - dateB;
      return 0;
    });

    return result;
  }, [pastItems, filters, getPlayerName]);

  const selectableFilteredMatchIds = useMemo(
    () =>
      filteredPastItems
        .map(getSelectableMatchId)
        .filter((id): id is number => id != null),
    [filteredPastItems]
  );

  const highlightedPlayerIds = useMemo(() => {
    const terms = filters.search
      .split(",")
      .map((term) => term.trim().toLowerCase())
      .filter((term) => term.length > 0);
    if (terms.length === 0) return [];
    return players
      .filter((p) => terms.some((term) => p.name.toLowerCase().includes(term)))
      .map((p) => p.id);
  }, [filters.search, players]);

  const hasActiveFilters =
    Boolean(filters.search) ||
    filters.players.length > 0 ||
    filters.sortBy !== "date-desc" ||
    Boolean(filters.dateRange?.start && filters.dateRange?.end);

  const resetFilters = () => {
    setFilters({
      search: "",
      players: [],
      sortBy: "date-desc",
      dateRange: { start: null, end: null },
    });
  };

  const handleCheckboxChange = (matchId: number, checked: boolean | string) => {
    setSelectedMatchIds((prev) => {
      if (checked) return [...prev, matchId];
      return prev.filter((id) => id !== matchId);
    });
  };

  const handleBulkDeleteClick = () => {
    if (selectedMatchIds.length > 0) setIsBulkDeleteModalOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedMatchIds.length === 0 || !groupId) return;

    setIsProcessing(true);
    try {
      await cancelEventsLinkedToMatches(selectedMatchIds);
      await Promise.all(selectedMatchIds.map((id) => API.deleteMatch(id, groupId)));
      success({
        title: t("matches.bulkDeleteSuccess", "{{count}} match(es) deleted successfully!", {
          count: selectedMatchIds.length,
        }),
      });
      setSelectedMatchIds([]);
      setIsBulkDeleteModalOpen(false);
      setSelected(null);
      await load({ silent: true });
    } catch (err) {
      console.error("Bulk delete error:", err);
      toastError({
        title: t("matches.bulkDeleteError", "Error deleting selected matches"),
        description: err instanceof Error ? err.message : t("errors.error"),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const openPastEventDetail = async (context: CompletedMatchContext) => {
    let resolved = context;
    if (
      resolved.match == null &&
      resolved.event.match_id != null &&
      groupId
    ) {
      try {
        const fetched = (await API.getMatch(resolved.event.match_id, groupId)) as MatchWithExtras;
        resolved = { ...resolved, match: fetched };
      } catch (err) {
        console.error("Failed to load linked match:", err);
      }
    }

    setSelected({ kind: "past-event", context: resolved });
    setDetailHistory(
      resolved.match ? await fetchMatchResultHistory(resolved.match.id, groupId) : []
    );
  };

  const openLegacyDetail = async (match: MatchRecord) => {
    setSelected({ kind: "legacy", match });
    setDetailHistory(await fetchMatchResultHistory(match.id, groupId));
  };

  const openCreateMatch = () => {
    setWizardMode("create");
    setWizardInitial(defaultMatchFormData);
    setWizardPrefillTeams(undefined);
    setWizardContext({});
    setWizardEventGuests([]);
    setWizardOpen(true);
  };

  const openDeleteConfirm = (matchId: number) => {
    setDeleteTargetId(matchId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteTargetId == null || !groupId) return;

    setIsProcessing(true);
    try {
      await cancelEventsLinkedToMatches([deleteTargetId]);
      await API.deleteMatch(deleteTargetId, groupId);
      success({ title: t("matches.deleteSuccess") });
      setDeleteModalOpen(false);
      setDeleteTargetId(null);
      setSelected(null);
      await load({ silent: true });
    } catch (err) {
      console.error("Match delete error:", err);
      toastError({
        title: t("matches.deleteError"),
        description: err instanceof Error ? err.message : t("errors.error"),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const openDismissConfirm = (eventId: string, matchId?: number) => {
    setDismissTarget({ eventId, matchId });
    setDismissModalOpen(true);
  };

  const handleDismissConfirm = async () => {
    if (!dismissTarget || !groupId) return;

    setIsProcessing(true);
    try {
      if (dismissTarget.matchId != null) {
        try {
          await API.deleteMatch(dismissTarget.matchId, groupId);
        } catch (err) {
          console.error("Failed to delete empty match row:", err);
        }
      }
      await cancelMatchEvent(dismissTarget.eventId);
      success({ title: t("v2.matches.dismissEventSuccess") });
      setDismissModalOpen(false);
      setDismissTarget(null);
      setSelected(null);
      await load({ silent: true });
    } catch (err) {
      console.error("Dismiss event error:", err);
      toastError({
        title: t("v2.matches.dismissEventError"),
        description: err instanceof Error ? err.message : t("errors.error"),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const openLogResult = async (item: SelectedItem) => {
    let event: MatchEvent | undefined;
    let match: MatchRecord | null = null;
    let eventId: string | undefined;
    let matchId: number | undefined;

    if (item.kind === "past-event") {
      event = item.context.event;
      match = item.context.match;
      eventId = event.id;
      matchId = match?.id;
    } else {
      match = item.match;
      matchId = match.id;
    }

    let goingPlayerIds: number[] = [];
    let eventGuestNames: string[] = [];
    if (event) {
      const rsvps = await fetchRsvpsForEvent(event.id);
      goingPlayerIds = rsvps
        .filter((r) => r.status === "going" && r.player_id != null)
        .map((r) => r.player_id as number);
      eventGuestNames = rsvps
        .filter((r) => r.status === "going" && r.guest_name?.trim())
        .map((r) => r.guest_name!.trim());
    }

    const initial = event
      ? eventToFormData(event, match, goingPlayerIds)
      : matchToFormData(match!);

    let prefill: { teamA: number[]; teamB: number[] } | undefined;
    if (!match && event && hasPublishedLineup(event)) {
      const parsed = parseEventLineup(event.lineup, { playerNameById: new Map() });
      prefill = {
        teamA: parsed.playerIdsA,
        teamB: parsed.playerIdsB,
      };
    }

    setWizardMode(matchId != null ? "edit" : "create");
    setWizardInitial(initial);
    setWizardPrefillTeams(prefill);
    setWizardContext({ eventId, matchId });
    setWizardEventGuests(eventGuestNames.map((name) => ({ name })));
    setWizardOpen(true);
  };

  const handleFormSubmit = async (formData: MatchFormData) => {
    const hasScore = formData.scoreA !== "" && formData.scoreB !== "";
    const hasWinner = isMatchWinner(formData.winner);
    const hasGoalscorers = (formData.goalscorers?.length || 0) > 0;

    if (!formData.date || !(hasScore || hasWinner || hasGoalscorers)) {
      toastError({
        title: t("matches.invalidForm"),
        description: t("matches.formRequirements"),
      });
      return;
    }

    if (!groupId) {
      toastError({ title: t("errors.error"), description: t("groups.noGroupSelected") });
      return;
    }

    setIsProcessing(true);

    try {
      const roster = [...playerNames];
      const guestNames = formData.guestPlayerNames;
      const allIds = [
        ...formData.teamA,
        ...formData.teamB,
        ...(formData.goalscorers ?? []).map((g) => g.playerId),
      ];
      const idMap = await resolveMatchPlayerIdMap(allIds, guestNames, groupId, roster);

      const teamA = formData.teamA
        .map((id) => idMap.get(id))
        .filter((id): id is number => id != null);
      const teamB = formData.teamB
        .map((id) => idMap.get(id))
        .filter((id): id is number => id != null);
      const resolvedGoalscorers = (formData.goalscorers ?? [])
        .map((g) => {
          const playerId = idMap.get(g.playerId);
          return playerId != null ? { ...g, playerId } : null;
        })
        .filter((g): g is NonNullable<typeof g> => g != null);

      const scorea = formData.scoreA === "" ? null : parseInt(String(formData.scoreA), 10);
      const scoreb = formData.scoreB === "" ? null : parseInt(String(formData.scoreB), 10);
      let winner = formData.winner;
      if (winner === undefined && scorea !== null && scoreb !== null) {
        if (scorea > scoreb) winner = "A";
        else if (scoreb > scorea) winner = "B";
        else winner = "Draw";
      }

      const dateObj = new Date(`${formData.date}T12:00:00Z`);
      const payload = {
        match_date: dateObj.toISOString(),
        teama: teamA,
        teamb: teamB,
        scorea,
        scoreb,
        winner: winner ?? null,
        goalscorers: resolvedGoalscorers,
        youtubelink: formData.youtubeLink || null,
        group_id: groupId,
      };

      let saved: MatchRecord;

      if (wizardContext.matchId) {
        saved = (await API.updateMatch(wizardContext.matchId, payload, groupId)) as MatchRecord;
        success({ title: t("matches.updateSuccess") });
      } else {
        saved = (await API.createMatch(payload)) as MatchRecord;
        if (wizardContext.eventId) {
          await linkMatchToEvent(wizardContext.eventId, saved.id);
        }
        success({ title: t("matches.addSuccess") });
      }

      setWizardOpen(false);
      setSelected(null);
      await load({ silent: true });
    } catch (err) {
      console.error("Match save error:", err);
      toastError({
        title: wizardContext.matchId ? t("matches.updateError") : t("matches.addError"),
        description: err instanceof Error ? err.message : t("errors.error"),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const formatMatchResultLabel = (match: MatchRecord) => {
    if (match.scorea != null && match.scoreb != null) {
      return `${match.scorea} - ${match.scoreb}`;
    }
    if (match.winner === "A") return t("matches.teamAWon");
    if (match.winner === "B") return t("matches.teamBWon");
    if (match.winner === "Draw") return t("matches.draw");
    return null;
  };

  const openDetailFromItem = (item: PastItem) => {
    if (item.kind === "event") {
      void openPastEventDetail(item.context);
    } else {
      void openLegacyDetail(item.match);
    }
  };

  const openEditFromItem = (item: PastItem) => {
    void openLogResult(pastItemToSelected(item));
  };

  const renderPastGridCard = (item: PastItem) => {
    const matchId = getSelectableMatchId(item);
    const isSelected = matchId != null && selectedMatchIds.includes(matchId);
    const { teamA, teamB } = getItemTeams(item);
    const missingResult = item.kind === "event" && Boolean(item.context.missingResult);
    const eventId = item.kind === "event" ? item.context.event.id : undefined;
    const canDismiss = item.kind === "event" && matchId == null && eventId != null;

    return (
      <MatchGridCard
        key={getItemKey(item)}
        date={getItemDate(item)}
        teamA={teamA}
        teamB={teamB}
        match={getItemMatch(item)}
        goalscorers={getItemGoalscorers(item)}
        matchId={matchId}
        missingResult={missingResult}
        isSelected={isSelected}
        highlightedPlayerIds={highlightedPlayerIds}
        getPlayerName={getPlayerName}
        onSelectChange={matchId != null ? handleCheckboxChange : undefined}
        onOpenDetail={() => openDetailFromItem(item)}
        onEdit={() => openEditFromItem(item)}
        onDelete={matchId != null ? () => openDeleteConfirm(matchId) : undefined}
        onDismiss={canDismiss ? () => openDismissConfirm(eventId) : undefined}
      />
    );
  };

  const renderPastRow = (item: PastItem) => {
    const matchId = getSelectableMatchId(item);
    const isSelected = matchId != null && selectedMatchIds.includes(matchId);
    const dateLabel = format(getItemDate(item), "EEE d MMM yyyy");

    let resultLabel: string | null = null;
    if (item.kind === "legacy") {
      resultLabel = formatMatchResultLabel(item.match);
    } else if (item.context.match) {
      resultLabel = formatMatchResultLabel(item.context.match);
    }

    const key = getItemKey(item);

    const openDetail = () => openDetailFromItem(item);

    const missingResult = item.kind === "event" && Boolean(item.context.missingResult);

    return (
      <div
        key={key}
        className={cn(
          "flex items-center gap-2 rounded-lg border bg-card px-3 py-2 transition-colors hover:border-soccer-primary/50",
          isSelected && "border-soccer-primary/50 bg-accent/30",
          missingResult && "border-amber-400/40"
        )}
      >
        {matchId != null && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => handleCheckboxChange(matchId, checked)}
            onClick={(e) => e.stopPropagation()}
            aria-label={t("matches.selectMatch", "Select match")}
          />
        )}
        <button
          type="button"
          onClick={openDetail}
          className="flex min-h-[36px] min-w-0 flex-1 items-center justify-between gap-2 text-left"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5">
            <span className="shrink-0 text-sm font-medium">{dateLabel}</span>
            {resultLabel ? (
              <span className="text-sm font-bold text-soccer-primary">{resultLabel}</span>
            ) : missingResult ? (
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                {t("v2.matches.missingResultWarning")}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">
                {t("v2.matches.detail.noResult")}
              </span>
            )}
          </div>
          <FaChevronRight className="shrink-0 text-xs text-muted-foreground" />
        </button>
      </div>
    );
  };

  const formatScore = (scoreA: number | null, scoreB: number | null) =>
    `${scoreA ?? "—"} - ${scoreB ?? "—"}`;

  const renderResultHistory = () => (
    <div className="rounded-lg border bg-accent/30 p-3 space-y-3">
      <p className="text-sm font-bold flex items-center gap-2">
        <FaHistory className="text-soccer-primary" />
        {t("v2.matches.resultHistory")}
      </p>
      {detailHistory.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("v2.matches.resultHistoryEmpty")}</p>
      ) : (
        <ul className="space-y-2">
          {detailHistory.map((edit) => (
            <li
              key={edit.id}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <span className="font-semibold">
                {formatScore(edit.previous_scorea, edit.previous_scoreb)} →{" "}
                {formatScore(edit.new_scorea, edit.new_scoreb)}
              </span>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {format(parseISO(edit.created_at), "d MMM · HH:mm")}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderDetail = () => {
    if (!selected) return null;

    const back = () => setSelected(null);

    if (selected.kind === "past-event") {
      const { event, match } = selected.context;

      return (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={back} className="gap-2 -ml-2">
            <FaArrowLeft /> {t("v2.matches.detail.back")}
          </Button>

          <Card>
            <CardHeader>
              <CardTitle>{format(parseISO(event.event_date), "EEEE d MMM yyyy")}</CardTitle>
              {event.location && (
                <CardDescription className="flex items-center gap-1">
                  <FaMapMarkerAlt /> {event.location}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {match && hasLoggedResult(match) ? (
                <>
                  <MatchCard
                    match={match}
                    players={playerNames}
                    canEdit={false}
                  />
                  {renderResultHistory()}
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      variant="outline"
                      className="flex-1 min-h-[44px]"
                      onClick={() => openLogResult(selected)}
                    >
                      <FaEdit className="mr-2" />
                      {t("v2.matches.editResult")}
                    </Button>
                    <Button
                      variant="outline"
                      className={cn("flex-1 min-h-[44px]", destructiveOutlineBtnClass)}
                      onClick={() => openDeleteConfirm(match.id)}
                    >
                      <FaTrashAlt className="mr-2" />
                      {t("matches.deleteMatch")}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">{t("v2.matches.detail.noResult")}</p>
                  <div className="flex flex-col gap-2">
                    <Button
                      className="w-full bg-soccer-primary text-black hover:bg-soccer-primary/90 min-h-[44px]"
                      onClick={() => openLogResult(selected)}
                    >
                      <FaFutbol className="mr-2" />
                      {t("v2.matches.logResult")}
                    </Button>
                    {match && (
                      <Button
                        variant="outline"
                        className={cn("w-full min-h-[44px]", destructiveOutlineBtnClass)}
                        onClick={() => openDeleteConfirm(match.id)}
                      >
                        <FaTrashAlt className="mr-2" />
                        {t("matches.deleteMatch")}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className={cn("w-full min-h-[44px]", destructiveOutlineBtnClass)}
                      onClick={() => openDismissConfirm(event.id, match?.id)}
                    >
                      <FaTrashAlt className="mr-2" />
                      {t("v2.matches.dismissEvent")}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    const { match } = selected;
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={back} className="gap-2 -ml-2">
          <FaArrowLeft /> {t("v2.matches.detail.back")}
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>{format(parseISO(match.match_date), "EEEE d MMM yyyy")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <MatchCard match={match} players={playerNames} canEdit={false} />
            {renderResultHistory()}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                className="flex-1 min-h-[44px]"
                onClick={() => openLogResult(selected)}
              >
                <FaEdit className="mr-2" />
                {t("v2.matches.editResult")}
              </Button>
              <Button
                variant="outline"
                className={cn("flex-1 min-h-[44px]", destructiveOutlineBtnClass)}
                onClick={() => openDeleteConfirm(match.id)}
              >
                <FaTrashAlt className="mr-2" />
                {t("matches.deleteMatch")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const deleteModal = (
    <ConfirmationModal
      isOpen={deleteModalOpen}
      onClose={() => {
        if (!isProcessing) {
          setDeleteModalOpen(false);
          setDeleteTargetId(null);
        }
      }}
      onConfirm={handleDeleteConfirm}
      title={t("matches.deleteMatch")}
      message={t("matches.deleteMatchConfirm", {
        id: deleteTargetId ?? "",
      })}
      confirmText={isProcessing ? t("common.deleting") : t("common.delete")}
      cancelText={t("common.cancel")}
      isDestructive
      isProcessing={isProcessing}
    />
  );

  const dismissModal = (
    <ConfirmationModal
      isOpen={dismissModalOpen}
      onClose={() => {
        if (!isProcessing) {
          setDismissModalOpen(false);
          setDismissTarget(null);
        }
      }}
      onConfirm={handleDismissConfirm}
      title={t("v2.matches.dismissEvent")}
      message={t("v2.matches.dismissEventConfirm")}
      confirmText={isProcessing ? t("common.deleting") : t("v2.matches.dismissEvent")}
      cancelText={t("common.cancel")}
      isDestructive
      isProcessing={isProcessing}
    />
  );

  const bulkDeleteModal = (
    <ConfirmationModal
      isOpen={isBulkDeleteModalOpen}
      onClose={() => {
        if (!isProcessing) setIsBulkDeleteModalOpen(false);
      }}
      onConfirm={handleBulkDeleteConfirm}
      title={t("matches.deleteSelectedMatches")}
      message={t("matches.deleteSelectedMatchesConfirm", {
        count: selectedMatchIds.length,
        defaultValue:
          "Are you sure you want to delete {{count}} selected match(es)? This action cannot be undone.",
      })}
      confirmText={
        isProcessing
          ? t("common.deleting")
          : t("matches.deleteMatches", "Delete {{count}} Matches", {
              count: selectedMatchIds.length,
            })
      }
      cancelText={t("common.cancel")}
      isDestructive
      isProcessing={isProcessing}
    />
  );

  if (!groupId) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        {t("groups.noGroupSelected")}
      </p>
    );
  }

  if (loading) {
    return <PageLoader label={t("common.loading")} />;
  }

  if (selected) {
    return (
      <>
        {renderDetail()}
        {deleteModal}
        {dismissModal}
        {bulkDeleteModal}
        <MatchFormWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          players={players}
          eventGuests={wizardEventGuests}
          initialData={wizardInitial}
          mode={wizardMode}
          isProcessing={isProcessing}
          prefillTeams={wizardPrefillTeams}
          onSubmit={handleFormSubmit}
        />
      </>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FaCalendarAlt className="text-soccer-primary" />
          {t("v2.matches.title")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{t("v2.matches.description")}</p>
      </div>

      <MatchFiltersToolbar
        filters={filters}
        onFiltersChange={setFilters}
        onReset={resetFilters}
        years={matchYears}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        selectedCount={selectedMatchIds.length}
        filteredCount={filteredPastItems.length}
        totalCount={pastItems.length}
        showFilters={pastItems.length > 0}
        showSelectAll={
          selectableFilteredMatchIds.length > 0 &&
          !selectableFilteredMatchIds.every((id) => selectedMatchIds.includes(id))
        }
        onSelectAll={() => setSelectedMatchIds(selectableFilteredMatchIds)}
        onClearSelection={() => setSelectedMatchIds([])}
        onBulkDelete={handleBulkDeleteClick}
        onRegisterMatch={openCreateMatch}
        isProcessing={isProcessing}
      />

      <section className={viewMode === "grid" ? "space-y-4" : "space-y-2"}>
        {pastItems.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center rounded-lg border border-dashed">
            {t("v2.matches.noPast")}
          </p>
        ) : filteredPastItems.length === 0 ? (
          <div className="space-y-3 py-4 text-center rounded-lg border border-dashed">
            <p className="text-sm text-muted-foreground">
              {t("matches.noMatchesMatchFilters", "No matches match your filters")}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={resetFilters}>
                {t("matches.clearFilters", "Clear Filters")}
              </Button>
            )}
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredPastItems.map(renderPastGridCard)}
          </div>
        ) : (
          filteredPastItems.map(renderPastRow)
        )}
      </section>

      <MatchFormWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        players={players}
        eventGuests={wizardEventGuests}
        initialData={wizardInitial}
        mode={wizardMode}
        isProcessing={isProcessing}
        prefillTeams={wizardPrefillTeams}
        onSubmit={handleFormSubmit}
      />

      {deleteModal}
      {dismissModal}
      {bulkDeleteModal}
    </div>
  );
}
